/* eslint-disable react/display-name */
import React, { useState, useMemo } from 'react';
import { useApolloClient } from '@apollo/client';
import {
  LabelValue,
  WbsLabelValueSearchResult,
  WbsLabelValueSearchInput
} from 'graphql/types';
import { TimesheetLabel } from 'modules/timesheet-labels/labelSchema';
import { mappedLabelValues, LabelValueType } from '../LabelFacetDetails';
import { SearchBoxProps } from '../type';
import { scoredSuggestions } from '../scoredSuggestions';
import {
  searchPrograms,
  searchProgramsByTerm
} from './hooks/useProgramLabelSearch';

export const cleanSearchTerm = (value: string): string =>
  value.replace(/['",]+/g, '');

type SearchProgramsHandler = (
  input: WbsLabelValueSearchInput
) => Promise<WbsLabelValueSearchResult[]>;

const matchOption = (
  searchPrograms: SearchProgramsHandler,
  labelId: string
) => async (_: LabelValue[], term: string): Promise<LabelValueType | null> => {
  if (!term) return null;

  const programs = await searchPrograms({ query: cleanSearchTerm(term) });
  const mappedValues = mappedLabelValues(programs, labelId);
  const option = mappedValues && mappedValues.length > 0 && mappedValues[0];
  return option && option.displayText ? option : null;
};

const withCriteriaProvider = (
  tag: string,
  context: React.Context<LabelCriteriaProviderContextType>,
  label: TimesheetLabel
) => (
  BaseComponent: React.FunctionComponent<SearchBoxProps>
): React.FC<SearchBoxProps> => (props: SearchBoxProps) => {
  const { searchCriteria: { criterions = {} } = {} } = props;
  const [selected, setSelected] = useState<LabelValueType[]>(
    (criterions[tag] as LabelValueType[]) || []
  );
  const apolloClient = useApolloClient();
  const $searchPrograms = searchPrograms({
    client: apolloClient
  });
  const searchProgramsSuggestion = searchProgramsByTerm({
    client: apolloClient
  });
  const value = useMemo(
    () => ({
      selected,
      setSelected,
      options: [],
      matchOption: matchOption($searchPrograms, label.id),
      searchPrograms: $searchPrograms,
      suggestions: scoredSuggestions(searchProgramsSuggestion)
    }),
    [selected, $searchPrograms, searchProgramsSuggestion]
  );

  return (
    <context.Provider value={value}>
      <BaseComponent {...props} />
    </context.Provider>
  );
};

type LabelCriteriaProviderContextType = {
  selected: LabelValueType[];
  setSelected: React.Dispatch<React.SetStateAction<LabelValueType[]>>;
  matchOption: (
    options: LabelValue[],
    term: string
  ) => Promise<LabelValueType | null>;
  searchPrograms: SearchProgramsHandler;
};

const makeCriteriaProvider = (label: TimesheetLabel) => (
  tag: string,
  context: React.Context<LabelCriteriaProviderContextType>
): ((
  BaseComponent: React.FunctionComponent<SearchBoxProps>
) => React.FC<SearchBoxProps>) => withCriteriaProvider(tag, context, label);

export default makeCriteriaProvider;
